import { useMemo } from 'react';

import {
  Form,
  SelectInput,
  Button,
  ICONS,
  TextInput,
  PLATFORM_THEME_COLORS,
  Icon,
  Loader,
  NumberInput,
} from '@thinknet-gmbh/platform-common-ui';
import { EmployeeStatusesEnum, PROTECTED_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import {
  DeleteContainer,
  FormListContent,
} from 'src/pages/jobs/components/job-form/job-form.styles';
import { filterOption } from 'src/common/helpers/common.helpers';
import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { IJobSupervisorsListProps } from 'src/pages/jobs/components/job-form/job-form.interfaces';
import { ButtonsWrapper } from 'src/pages/jobs/components/job-form/components/job-info-form-items/job-info-form-items.styles';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

import JobEmployeesList from './job-employees-list.component';

const JobSupervisorsList = ({
  parentField,
  t,
  parentFields,
  addLocation,
  initialValues,
  form,
}: IJobSupervisorsListProps) => {
  const currentCompanyId = useGetCurrentCompanyId();
  const { employees, loading } = useGetAllEmployeesForList({
    filter: { companyId: currentCompanyId, permissionGroupName: PROTECTED_PERMISSIONS.supervisor },
  });

  const filteredEmployees = useMemo(
    () =>
      employees.filter(
        (employee: any) => employee.employeePersonalInfo.status === EmployeeStatusesEnum.ACTIVE
      ),
    [employees]
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <Form.List
      name={[parentField, 'jobSupervisors']}
      initialValue={initialValues ? undefined : [{}]}
    >
      {(fields, { add: addSupervisor, remove: removeSupervisor }) => (
        <FormListContent isIcon={fields.length > 1}>
          {fields.map(field => (
            <div key={field.key}>
              <DeleteContainer isIcon={fields.length > 1}>
                <Form.Item
                  name={[field.name, 'supervisorJobTitle']}
                  label={t('jobs:labels.supervisorJobTitle')}
                  rules={[
                    { required: true, message: t('jobs:validation.supervisorJobTitleRequired') },
                  ]}
                >
                  <TextInput placeholder={t('jobs:labels.hrSiniorManager')} />
                </Form.Item>

                {fields.length > 1 && (
                  <Icon
                    icon={ICONS.trash}
                    size={22}
                    rightMargin={false}
                    color={PLATFORM_THEME_COLORS.tone.red}
                    onClick={() => removeSupervisor(field.name)}
                  />
                )}
              </DeleteContainer>

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const formJobSupervisors = getFieldValue([
                    'jobLocations',
                    parentField,
                    'jobSupervisors',
                  ]);
                  const supervisorMembersIds = formJobSupervisors
                    .map((supervisor: any) => supervisor?.supervisorMemberId)
                    .filter(Boolean);

                  return (
                    <Form.Item
                      name={[field.name, 'supervisorMemberId']}
                      label={t('jobs:labels.supervisorName')}
                    >
                      <SelectInput
                        showSearch
                        filterOption={filterOption}
                        placeholder={t('employee:labels.startTyping')}
                      >
                        {filteredEmployees.map((employee: any) => (
                          <SelectInput.Option
                            value={employee._id}
                            key={employee._id}
                            disabled={supervisorMembersIds.includes(employee._id)}
                          >
                            {employee.profile.displayName}
                          </SelectInput.Option>
                        ))}
                      </SelectInput>
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                name={[field.name, 'jobPlacesCount']}
                label={t('jobs:labels.jobPlaces')}
                rules={[{ required: true, message: t('jobs:validation.jobPlacesRequired') }]}
              >
                <NumberInput customInput={TextInput} maxLength={10} placeholder="2" />
              </Form.Item>
              <JobEmployeesList
                initialValues={initialValues}
                supervisorField={field.name}
                locationField={parentField}
              />
            </div>
          ))}
          <ButtonsWrapper>
            <Button
              type="text"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => addSupervisor()}
            >
              {t('jobs:buttons.addSupervisorName')}
            </Button>
            {parentField === parentFields.length - 1 && (
              <Button
                type="text"
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={() => addLocation({ supervisors: [{}] })}
              >
                {t('jobs:buttons.addLocation')}
              </Button>
            )}
          </ButtonsWrapper>
        </FormListContent>
      )}
    </Form.List>
  );
};

export default JobSupervisorsList;
